import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/layouts/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { 
  LayoutDashboard, ShoppingCart, DollarSign, Clock, TrendingUp,
  Utensils, QrCode, ChefHat, Users, Settings, ExternalLink, Copy
} from "lucide-react";
import { formatCurrency, cn } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [stats, setStats] = useState({
    today_orders: 0,
    today_revenue: 0,
    pending_orders: 0,
    menu_items: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resResponse, ordersResponse, itemsResponse] = await Promise.all([
        axios.get(`${API}/restaurant/profile`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/restaurant/items`),
      ]);
      
      setRestaurant(resResponse.data);
      
      const today = new Date().toISOString().split("T")[0];
      const todayOrders = ordersResponse.data.filter((o) => 
        o.created_at.startsWith(today)
      );
      const pendingOrders = ordersResponse.data.filter((o) => 
        ["new", "preparing"].includes(o.status)
      );
      const todayRevenue = todayOrders
        .filter((o) => o.payment_status === "paid")
        .reduce((sum, o) => sum + o.total, 0);
      
      setStats({
        today_orders: todayOrders.length,
        today_revenue: todayRevenue,
        pending_orders: pendingOrders.length,
        menu_items: itemsResponse.data.length,
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const statCards = [
    {
      title: "Today's Orders",
      value: stats.today_orders,
      icon: ShoppingCart,
      color: "text-orange-500",
      bgColor: "bg-orange-100",
    },
    {
      title: "Pending Orders",
      value: stats.pending_orders,
      icon: TrendingUp,
      color: "text-amber-500",
      bgColor: "bg-amber-100",
    },
    {
      title: "Revenue Today",
      value: formatCurrency(stats.today_revenue),
      icon: DollarSign,
      color: "text-green-500",
      bgColor: "bg-green-100",
    },
    {
      title: "Menu Items",
      value: stats.menu_items,
      icon: Utensils,
      color: "text-blue-500",
      bgColor: "bg-blue-100",
    },
  ];

  const quickActions = [
    {
      title: "Manage Menu",
      icon: Utensils,
      href: "/manager/menu",
      description: "Add or edit menu items",
      color: "text-gray-600",
      hoverColor: "hover:border-gray-400",
    },
    {
      title: "QR & Tables",
      icon: QrCode,
      href: "/manager/tables",
      description: "Generate table QR codes",
      color: "text-gray-600",
      hoverColor: "hover:border-gray-400",
    },
    {
      title: "Kitchen View",
      icon: ChefHat,
      href: "/kitchen",
      description: "Open kitchen display",
      color: "text-white",
      bgColor: "bg-orange-500",
      isHighlighted: true,
    },
    {
      title: "Customer View",
      icon: Users,
      href: `/r/${restaurant?.slug}`,
      description: "Preview customer menu",
      color: "text-gray-600",
      hoverColor: "hover:border-gray-400",
      external: true,
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="manager-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Welcome back!</h1>
          <p className="text-muted-foreground">Here's a summary of your restaurant's performance today.</p>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards.map((stat, index) => (
              <Card key={index} className="border shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.title}</p>
                      <p className="text-3xl font-bold mt-1">{stat.value}</p>
                    </div>
                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", stat.bgColor)}>
                      <stat.icon className={cn("w-6 h-6", stat.color)} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <Card className="border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {quickActions.map((action, index) => (
                action.isHighlighted ? (
                  <Link
                    key={index}
                    to={action.href}
                    className={cn(
                      "flex flex-col items-center justify-center p-6 rounded-xl transition-all",
                      action.bgColor,
                      "hover:opacity-90"
                    )}
                    data-testid={`quick-action-${action.title.toLowerCase().replace(" ", "-")}`}
                  >
                    <action.icon className={cn("w-8 h-8 mb-2", action.color)} />
                    <span className={cn("font-semibold", action.color)}>{action.title}</span>
                  </Link>
                ) : action.external ? (
                  <a
                    key={index}
                    href={action.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed border-gray-200 transition-all",
                      action.hoverColor
                    )}
                    data-testid={`quick-action-${action.title.toLowerCase().replace(" ", "-")}`}
                  >
                    <action.icon className={cn("w-8 h-8 mb-2", action.color)} />
                    <span className="font-medium text-gray-700">{action.title}</span>
                  </a>
                ) : (
                  <Link
                    key={index}
                    to={action.href}
                    className={cn(
                      "flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed border-gray-200 transition-all",
                      action.hoverColor
                    )}
                    data-testid={`quick-action-${action.title.toLowerCase().replace(" ", "-")}`}
                  >
                    <action.icon className={cn("w-8 h-8 mb-2", action.color)} />
                    <span className="font-medium text-gray-700">{action.title}</span>
                  </Link>
                )
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Restaurant Info */}
        {restaurant && (
          <Card className="border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold">Restaurant Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Subdomain</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="font-medium text-primary">{restaurant.slug}.dalabsom.com</p>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => copyToClipboard(`${window.location.origin}/r/${restaurant.slug}`)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium mt-1">{restaurant.contact_email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium mt-1">{restaurant.address}, {restaurant.city}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium mt-1">{restaurant.contact_phone}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default ManagerDashboard;
